import { Anchor, Box, Divider, Group, Loading, Select } from '@byecode/ui'
import type { Field, IndependentAction, TypeInstanceMap } from '@lighthouse/core'
import {
    type AliyunMessageActionConfig,
    type FlowNode,
    generateJson,
    getInputInnerTypeMatch,
    getUpstreamActionsOptions,
    getUpstreamNodesOptions,
    InnerTypeMapByFieldType,
    ListItem4ByecodeUi,
    MatchEnableInnerType,
    NodeFieldPreview,
    type NodeTypes,
    useAtomData,
    VariableSelect
} from '@lighthouse/shared'
import { equals, find, isEmpty, uniq } from 'rambda'
import React, { useCallback, useMemo } from 'react'
import { useSetState } from 'react-use'

import { otherPlatformListAtom } from '@/atoms/application/state'
import { lastPageOfStackAtom } from '@/atoms/page/state'
import { workflowListAtom } from '@/atoms/workflow/state'
import { useFlowContainerContext } from '@/contexts/FlowContext'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'
import { useGetPageParameterAndVariableOptions, useGetUserRecordOptions } from '@/hooks/usePage'
import { useGetAliyunMessageInfo } from '@/shared/reusable'

import { ActionAuthIntegrateCard } from '../../Common/ActionAuthIntegrateCard'
import { GroupRequired } from '../../Common/GroupRequired'
import { UnitNoAuth } from '../../Common/UnitNoAuth'
import * as CM from '../../styles'
import * as SC from './styles'

const PHONE_FIELD: Field = { id: '', dsId: '', type: 'phoneNumber', name: '', innerType: InnerTypeMapByFieldType['phoneNumber'] }

interface AliyunMessageActionConfigureProps {
    allParentNodes?: FlowNode[]
    allParentActions?: IndependentAction[]
    nodeType?: NodeTypes
    actionTypeSwitcher?: React.ReactNode
    value: AliyunMessageActionConfig
    onChange: (value: AliyunMessageActionConfig) => void
}

interface State {
    mode: 'add' | 'update'
    opened: boolean
    id?: string
}

export const AliyunMessageActionConfigure: React.FunctionComponent<AliyunMessageActionConfigureProps> = ({
    actionTypeSwitcher,
    allParentNodes,
    allParentActions,
    value,
    onChange
}) => {
    const { type } = useFlowContainerContext()
    const isInFlow = !!type
    const { mobileList, signatureId, templateId, values } = value

    const disabledWithVersion = useIsDisabledWithVersion()
    const otherPlatformList = useAtomData(otherPlatformListAtom)

    const aliyunSmsAccount = useMemo(() => otherPlatformList?.find(item => item.type === 'ALIYUN_SMS'), [otherPlatformList])

    const aliyunTemplateId = templateId
    const { data, isLoading } = useGetAliyunMessageInfo(aliyunSmsAccount?.id)

    const aliyunMessageSignaturesOptions = useMemo(() => {
        return data?.signatures?.map(item => ({
            value: item.name,
            label: item.name
        }))
    }, [data?.signatures])

    const aliyunMessageTemplatesOptions = useMemo(() => {
        return (
            data?.templates.map(item => ({
                value: item.id,
                label: item.name,
                content: item.content
            })) ?? []
        )
    }, [data?.templates])

    const currentAliyunMessageTemplateOption = useMemo(() => {
        return find(option => option.value === aliyunTemplateId, aliyunMessageTemplatesOptions)
    }, [aliyunMessageTemplatesOptions, aliyunTemplateId])

    const currentAliyunMessageTemplate = useMemo(() => {
        return currentAliyunMessageTemplateOption?.content ?? ''
    }, [currentAliyunMessageTemplateOption?.content])

    const [{ opened, mode, id }, setState] = useSetState<State>({
        opened: false,
        mode: 'add'
    })

    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const workflowList = useAtomData(workflowListAtom)
    const pageId = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => (isInFlow ? '' : s?.pageId || ''), [isInFlow])
    )

    const userOption = useGetUserRecordOptions()

    const textUpstreamOptions = useMemo(() => {
        if (isInFlow) {
            return getUpstreamNodesOptions({
                workflowList,
                dataSourceList,
                upstreamNodes: allParentNodes || [],
                allUpstreamNodes: allParentNodes || []
            })
        }
        return getUpstreamActionsOptions({
            workflowList,
            allParentActions: allParentActions || [],
            dataSourceList
        })
    }, [allParentActions, allParentNodes, dataSourceList, isInFlow, workflowList])

    const pageOptions = useGetPageParameterAndVariableOptions({ pageId })

    const handleUpdate = useCallback(
        (value: string) => {
            setState({ opened: true, mode: 'update', id: value })
        },
        [setState]
    )

    const handleAdd = useCallback(() => {
        setState({ opened: true, mode: 'add', id: undefined })
    }, [setState])

    const handleTemplateChange = useCallback(
        (nextTemplateId: string) => {
            const currentOption = find(option => option.value === nextTemplateId, aliyunMessageTemplatesOptions)?.content ?? ''
            const variablesMatches = currentOption.match(/\${(\w+)}/g) ?? []
            const variables = variablesMatches.map(item => item.replace(/\${(\w+)}/g, '$1'))

            const uniqVariables = uniq(variables)

            const valuesVariableNames = (values || []).map(v => v.variableName)

            if (equals(valuesVariableNames, uniqVariables) && valuesVariableNames.length > 0) {
                onChange({ ...value, templateId: nextTemplateId })
                return
            }
            const newValues = uniqVariables.map(field => {
                const preservedValue = find(v => v.variableName === field, values)?.value ?? {}
                return { variableName: field, value: preservedValue }
            })
            onChange({ ...value, templateId: nextTemplateId, values: newValues })
        },
        [aliyunMessageTemplatesOptions, onChange, value, values]
    )

    // const aliyunSms = useMemo(() => {
    //     // const platforms = (otherPlatform?.map(item => item.type) ?? []);
    //     return unitList.find(item => item.unitType === 'ALIYUN_SMS', platforms));
    //   }, [otherPlatform])

    const header = useMemo(() => {
        if (!actionTypeSwitcher) {
            return null
        }
        return (
            <>
                <Group label="动作配置">
                    <ListItem4ByecodeUi>
                        <CM.FormItemLabelWrapper>
                            <CM.FormItemLabel>类型</CM.FormItemLabel>
                        </CM.FormItemLabelWrapper>
                        <CM.FormItemContent>{actionTypeSwitcher}</CM.FormItemContent>
                    </ListItem4ByecodeUi>
                </Group>
                <Divider />
            </>
        )
    }, [actionTypeSwitcher])

    if (!aliyunSmsAccount) {
        return (
            <>
                {header}
                <UnitNoAuth type="ALIYUN_SMS" />
            </>
        )
    }

    return (
        <>
            {header}
            <ActionAuthIntegrateCard type="ALIYUN_SMS" />
            <Divider color="var(--color-gray-200)" />
            <GroupRequired required label="手机号">
                <Box px={8}>
                    <VariableSelect
                        disabled={disabledWithVersion}
                        width="100%"
                        disablePexels
                        style={{ marginTop: 8 }}
                        field={PHONE_FIELD}
                        innerTypes={getInputInnerTypeMatch(PHONE_FIELD.innerType)}
                        pageOptions={pageOptions}
                        upstreamOptions={textUpstreamOptions}
                        userOption={userOption}
                        value={mobileList}
                        onChange={mobileList => onChange({ ...value, mobileList })}
                    />
                    <SC.Tip>可以输入多个手机号，给多个手机号发送短信，手机号之间用中/英文逗号隔开，或选择手机号字段。</SC.Tip>
                </Box>
            </GroupRequired>
            <Divider color="var(--color-gray-200)" />
            <GroupRequired
                required
                label="短信签名"
                styles={{
                    root: {
                        padding: 0
                    }
                }}
            >
                <Box px={8}>
                    <Select
                        emptyComponent={isLoading && <Loading />}
                        style={{ marginTop: 8 }}
                        placeholder="请选择"
                        value={signatureId}
                        onChange={signatureId => onChange({ ...value, signatureId })}
                        options={aliyunMessageSignaturesOptions}
                    />
                    <SC.Tip>
                        需先在阿里云添加短信签名
                        <Anchor to="https://help.aliyun.com/zh/sms/user-guide/create-signatures" target="_blank">
                            详见 &gt;
                        </Anchor>
                    </SC.Tip>
                </Box>
            </GroupRequired>
            <Divider color="var(--color-gray-200)" />
            <GroupRequired required label="短信模板">
                <Box px={8}>
                    <Select
                        style={{ marginTop: 8 }}
                        placeholder="请选择"
                        value={templateId}
                        onChange={templateId => handleTemplateChange(templateId)}
                        options={aliyunMessageTemplatesOptions}
                    />
                    <SC.Tip>
                        需先在阿里云添加短信模板
                        <Anchor
                            to="https://help.aliyun.com/zh/sms/user-guide/create-message-templates-1?spm=a2c4g.11186623.0.i5"
                            target="_blank"
                        >
                            详见 &gt;
                        </Anchor>
                    </SC.Tip>
                    <SC.MessageTemplatePreviewer>
                        <SC.MessageTemplatePreviewerHeader>短信模板内容预览：</SC.MessageTemplatePreviewerHeader>
                        <SC.MessageTemplatePreviewerContent>{currentAliyunMessageTemplate}</SC.MessageTemplatePreviewerContent>
                    </SC.MessageTemplatePreviewer>
                </Box>
            </GroupRequired>
            {!!(values?.length || 0) && (
                <>
                    <Divider color="var(--color-gray-200)" />
                    <GroupRequired
                        required
                        label="短信模板变量对应的实际值"
                        styles={{
                            root: {
                                marginBottom: 20
                            }
                        }}
                    >
                        <Box px={8}>
                            {values.map((field, index) => (
                                <SC.VariableItem style={{ marginTop: 8 }} key={field.variableName}>
                                    <SC.VariableTitle>{`\${${field.variableName}}`}</SC.VariableTitle>
                                    <NodeFieldPreview
                                        innerTypes={MatchEnableInnerType}
                                        placeholder="请输入"
                                        value={isEmpty(field.value) ? generateJson('') : field.value}
                                        onBlur={nextValue => {
                                            const nextValues = values.map((item, i) => (i === index ? { ...item, value: nextValue } : item))
                                            onChange({ ...value, values: nextValues })
                                        }}
                                        autoHeight
                                        pageOptions={pageOptions}
                                        upstreamOptions={textUpstreamOptions}
                                        userOption={userOption}
                                        dataSourceList={dataSourceList}
                                    />
                                </SC.VariableItem>
                            ))}
                        </Box>
                    </GroupRequired>
                </>
            )}
        </>
    )
}
